import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { tradingService } from '../../services/tradingService';

// Interest-Income ledger — record cash income (interest on idle cash) for an account. This is
// INCOME → EQUITY (like a dividend): it folds into the account TWR + NAV, so the reported return
// and cash reconcile with the broker/Bloomberg. Never edited in place: a partial correction is a NEW row with a
// negative amount; a MISBOOKED row can be deleted (owner 2026-10-16) — that removes it from the account's cash and
// its return from that date onward, is audited server-side, and the stored curves refresh.
const fmt$ = (n) =>
  (n == null || isNaN(n)) ? '—'
    : `${n < 0 ? '−' : ''}$${Math.abs(Number(n)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const IncomeAccrualPanel = ({ accountId, accountName }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [date, setDate] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!accountId) { setRows([]); return; }
    setLoading(true); setErr('');
    tradingService.listAccountIncome(accountId)
      .then((r) => { const d = r.data; setRows(Array.isArray(d) ? d : (d.results || [])); })
      .catch(() => setErr('Could not load income accruals.'))
      .finally(() => setLoading(false));
  }, [accountId]);
  useEffect(() => { load(); }, [load]);

  // Chronological running total = income-to-date.
  const withTotal = useMemo(() => {
    let bal = 0;
    return [...rows]
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.id - b.id))
      .map((r) => { bal += Number(r.amount); return { ...r, total: bal }; });
  }, [rows]);
  const total = withTotal.length ? withTotal[withTotal.length - 1].total : 0;

  const add = async () => {
    const amt = parseFloat(amount);
    if (!date) { setErr('Pick a date.'); setOk(''); return; }
    if (isNaN(amt) || amt === 0) { setErr('Amount must be non-zero (use a negative amount to correct a prior entry).'); setOk(''); return; }
    setBusy(true); setErr(''); setOk('');
    try {
      await tradingService.createAccountIncome({
        account: accountId, date, amount: amt.toFixed(4),
        kind: 'interest', note: note.trim(),
      });
      setAmount(''); setNote(''); setOk('Interest income recorded — it now folds into the account TWR + NAV.');
      load();
    } catch (e) {
      const d = e?.response?.data;
      setErr((d && (d.account || d.amount || d.detail)) || 'Could not save the income accrual.');
    } finally { setBusy(false); }
  };

  const remove = async (r) => {
    const ok_ = window.confirm(
      `Delete the ${fmt$(Number(r.amount))} income dated ${r.date}?\n\n` +
      `It comes out of ${accountName || 'the account'}'s cash and out of its return from ${r.date} onward ` +
      `(the performance curve is recomputed). The deletion is recorded in the audit log.`);
    if (!ok_) return;
    setBusy(true); setErr(''); setOk('');
    try {
      await tradingService.deleteAccountIncome(r.id);
      setOk('Income row deleted — the account NAV and return are being recomputed.');
      load();
    } catch (e) {
      const d = e?.response?.data;
      setErr((d && d.detail) || 'Could not delete the income row.');
    } finally { setBusy(false); }
  };

  const inputCls = 'rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#161b22] px-2.5 py-1.5 text-sm text-gray-900 dark:text-gray-100';

  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-baseline justify-between">
        <div>
          <h3 className="text-base font-serif text-gray-900 dark:text-gray-100">Interest Income</h3>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 uppercase tracking-wider">
            {accountName || 'account'} · interest on idle cash · income → return + NAV
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Income to date</div>
          <div className={`text-lg font-semibold tabular-nums ${total >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{fmt$(total)}</div>
        </div>
      </div>

      {err && <div className="mx-5 mt-3 rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-xs text-red-600 dark:text-red-400">{err}</div>}
      {ok && <div className="mx-5 mt-3 rounded-md border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-950/30 px-3 py-2 text-xs text-green-600 dark:text-green-400">{ok}</div>}

      {/* add row */}
      <div className="px-5 py-3 flex flex-wrap items-end gap-2 border-b border-gray-100 dark:border-gray-800/60">
        <label className="flex flex-col gap-0.5"><span className="text-[9px] uppercase tracking-wider text-gray-500">Recognition date</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} /></label>
        <label className="flex flex-col gap-0.5"><span className="text-[9px] uppercase tracking-wider text-gray-500">Amount (USD)</span>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder="0.00" className={`${inputCls} w-36`} /></label>
        <label className="flex flex-col gap-0.5 flex-1 min-w-[10rem]"><span className="text-[9px] uppercase tracking-wider text-gray-500">Note</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. interest income — Bloomberg cash, through 2026-08-31" className={inputCls} /></label>
        <button onClick={add} disabled={busy || !accountId}
          className="px-4 py-1.5 text-xs rounded-md bg-brand-gold text-black font-medium hover:bg-brand-gold/90 disabled:opacity-40">
          {busy ? 'Saving…' : 'Add income'}
        </button>
      </div>
      <div className="px-5 py-1.5 text-[10px] text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-800/60">
        Rows are never edited. To correct part of an entry, add a row with a negative amount; to remove a misbooked entry, delete it.
      </div>

      {/* table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-100 dark:border-gray-800/60">
              <th className="text-left font-medium px-5 py-2">Date</th>
              <th className="text-right font-medium px-3 py-2">Amount</th>
              <th className="text-right font-medium px-3 py-2">Cumulative</th>
              <th className="text-left font-medium px-3 py-2">Note</th>
              <th className="text-left font-medium px-3 py-2">By</th>
              <th className="px-3 py-2" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-5 py-6 text-center text-gray-400 text-xs">Loading…</td></tr>}
            {!loading && withTotal.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-6 text-center text-gray-400 text-xs">No interest income recorded yet — add the first entry above.</td></tr>
            )}
            {withTotal.map((r) => (
              <tr key={r.id} className="border-b border-gray-50 dark:border-gray-800/40 hover:bg-gray-50 dark:hover:bg-gray-800/30">
                <td className="px-5 py-2 tabular-nums text-gray-700 dark:text-gray-300">{r.date}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${Number(r.amount) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{fmt$(Number(r.amount))}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmt$(r.total)}</td>
                <td className="px-3 py-2 text-gray-500 truncate max-w-[18rem]">{r.note || '—'}</td>
                <td className="px-3 py-2 text-[11px] text-gray-400">{r.created_by_username || '—'}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => remove(r)} disabled={busy}
                    className="text-[11px] px-2 py-0.5 rounded border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-40">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IncomeAccrualPanel;
